import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { useJobs, useVm, useVmHealth } from '@/hooks/useDispatcher';
import { formatDateTime, formatRelative, maskSecret } from '@/lib/utils';

export default function VmDetailPage() {
  const { id } = useParams<{ id: string }>();
  const vm = useVm(id);
  const health = useVmHealth(id);
  const recentJobs = useJobs({ vm: vm.data?.slug });

  return (
    <div>
      <Link to="/vms" className="text-xs text-gray-500 hover:text-brand-dark inline-flex items-center gap-1 mb-3">
        <ArrowLeft size={12} /> Retour aux VMs
      </Link>
      <PageHeader
        title={vm.data?.slug ?? '…'}
        description={vm.data?.base_url ? maskSecret(vm.data.base_url, 32) : undefined}
        action={vm.data ? <StatusBadge status={vm.data.status} /> : null}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-xs text-gray-400">Région</div>
          <div className="font-heading text-brand-dark text-lg mt-1">{vm.data?.region ?? '—'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-gray-400">Poids</div>
          <div className="font-heading text-brand-dark text-lg mt-1">{vm.data?.weight ?? '—'}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-gray-400">Charge</div>
          <div className="font-heading text-brand-dark text-lg mt-1">
            {vm.data ? `${vm.data.active_jobs}/${vm.data.max_concurrent}` : '—'}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-gray-400">Dernier ping</div>
          <div className="font-heading text-brand-dark text-lg mt-1">{formatRelative(vm.data?.last_seen_at)}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="lg:col-span-2">
          <CardHeader title="Activité (24h)" />
          <CardContent>
            {health.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={health.data ?? []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="ts" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip />
                  <Area type="monotone" dataKey="active_jobs" stroke="#55b88b" fill="#eaf6ef" />
                  <Area type="monotone" dataKey="queue_depth" stroke="#3b82f6" fill="#dbeafe" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Métriques système" />
          <CardContent className="space-y-3">
            {health.data?.length ? (
              <>
                <Metric label="CPU" value={`${health.data.at(-1)?.cpu_pct ?? '—'}%`} />
                <Metric label="Mémoire" value={`${health.data.at(-1)?.mem_pct ?? '—'}%`} />
                <Metric label="Queue" value={String(health.data.at(-1)?.queue_depth ?? '—')} />
                <Metric label="Jobs actifs" value={String(health.data.at(-1)?.active_jobs ?? '—')} />
              </>
            ) : (
              <div className="text-xs text-gray-400">Pas de données récentes</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader title="Jobs récents" subtitle="Dernières exécutions sur cette VM" />
        <div className="divide-y divide-brand-border">
          {recentJobs.isLoading ? (
            <div className="p-5">
              <Skeleton className="h-20 w-full" />
            </div>
          ) : !recentJobs.data?.length ? (
            <div className="px-5 py-8 text-center text-xs text-gray-400">Aucun job récent</div>
          ) : (
            recentJobs.data.slice(0, 10).map((j) => (
              <Link
                key={j.id}
                to={`/jobs/${j.id}`}
                className="flex items-center justify-between px-5 py-3 table-row-hover"
              >
                <div>
                  <div className="text-sm font-medium text-brand-dark">{j.industry}</div>
                  <div className="text-xs text-gray-400">
                    {j.location_query} · {j.source}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={j.status} />
                  <span className="text-xs text-gray-400">{formatDateTime(j.created_at)}</span>
                </div>
              </Link>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="font-heading text-sm font-bold text-brand-dark">{value}</span>
    </div>
  );
}
